// AssoGazette.jsx — Page /dashboard/gazette (newsfeed)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/usePermissions';
import { toast } from 'sonner';
import {
  Newspaper, Loader2, Lock, Plus, MessageSquare, Send, Trash2, Pin, Heart, ThumbsUp, Award, Lightbulb, X, Image as ImageIcon, Sparkles,
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';

const REACTIONS = [
  { type: 'like',  icon: ThumbsUp, label: 'J\'aime',   color: '#3b82f6' },
  { type: 'love',  icon: Heart,    label: 'Adoré',     color: '#ef4444' },
  { type: 'bravo', icon: Award,    label: 'Bravo',     color: '#f59e0b' },
  { type: 'idee',  icon: Lightbulb, label: 'Idée',     color: '#84cc16' },
];

export default function AssoGazette() {
  const { user } = useAuth();
  const { can, loading: permsLoading } = usePermissions();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [showAI, setShowAI] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/gazette').then((r) => setPosts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (!permsLoading) load(); }, [permsLoading]);

  if (permsLoading) return <div className="p-8" />;
  if (!can('gazette', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="gazette-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès refusé</h3>
        </div>
      </div>
    );
  }
  const canModerate = can('gazette', 'moderate');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="gazette-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Gazette</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Le fil de l'asso</h1>
        <p className="text-sm text-[#475569] mt-2">Actualités, partages, retours d'expérience entre membres.</p>
      </div>

      {can('gazette', 'post') && (
        <button onClick={() => setShowCompose(true)} className="w-full card text-left mb-3 flex items-center gap-3 hover:bg-[#F9F9F8]" data-testid="gazette-new">
          <div className="w-10 h-10 rounded-full grid place-items-center text-white font-black" style={{ background: 'var(--theme-primary)' }}>
            {(user?.prenom?.[0] || '?').toUpperCase()}
          </div>
          <span className="text-sm text-[#475569]">Partagez quelque chose avec les membres...</span>
          <Plus className="w-4 h-4 text-[#94A3B8] ml-auto" />
        </button>
      )}

      {/* Assistant IA — visible uniquement pour le bureau */}
      {can('gazette', 'moderate') && (
        <button
          onClick={() => setShowAI(true)}
          className="w-full card text-left mb-5 flex items-center gap-3 hover:bg-[#F9F9F8] !bg-gradient-to-r !from-cyan-50/40 !to-violet-50/40 !border-cyan-200/50"
          data-testid="gazette-ai-trigger"
        >
          <div className="w-10 h-10 rounded-full grid place-items-center text-white shrink-0" style={{ background: 'linear-gradient(135deg, var(--theme-primary), #8b5cf6)' }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#0F172A]">Assistant IA du bureau</div>
            <div className="text-xs text-[#475569] truncate">Résumer la gazette · Suggérer des sujets de réunion</div>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] hidden sm:block">Claude Sonnet</span>
        </button>
      )}

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : posts.length === 0 ? (
        <div className="card text-center py-12 text-sm text-[#475569]" data-testid="gazette-empty">
          <Newspaper className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          Aucune publication.
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => <PostCard key={p.id} post={p} currentUser={user} canModerate={canModerate} onChanged={load} />)}
        </div>
      )}

      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} onSaved={() => { setShowCompose(false); load(); }} />}
      {showAI && <AIBureauModal onClose={() => setShowAI(false)} />}
    </div>
  );
}

function AIBureauModal({ onClose }) {
  const [tab, setTab] = useState('resume');
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const [sujets, setSujets] = useState(null);

  const runResume = async () => {
    setLoading(true); setResume(null);
    try {
      const { data } = await api.post('/assistant/resume-gazette');
      setResume(data);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur IA'); }
    finally { setLoading(false); }
  };

  const runSuggerer = async () => {
    setLoading(true); setSujets(null);
    try {
      const { data } = await api.post('/assistant/suggerer-reunion');
      setSujets(data.sujets || []);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur IA'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-start justify-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <div className="card max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="ai-bureau-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
            Assistant IA du bureau
          </h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 border-b border-[#F1F5F9] mb-4">
          <button
            onClick={() => setTab('resume')}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'resume' ? 'text-[#0F172A]' : 'text-[#475569] border-transparent'}`}
            style={tab === 'resume' ? { borderColor: 'var(--theme-primary)' } : {}}
            data-testid="ai-tab-resume"
          >Résumer la gazette</button>
          <button
            onClick={() => setTab('sujets')}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'sujets' ? 'text-[#0F172A]' : 'text-[#475569] border-transparent'}`}
            style={tab === 'sujets' ? { borderColor: 'var(--theme-primary)' } : {}}
            data-testid="ai-tab-sujets"
          >Suggérer 3 sujets de réunion</button>
        </div>

        {tab === 'resume' && (
          <div data-testid="ai-resume-pane">
            <p className="text-xs text-[#475569] mb-3">L'IA résume les 15 dernières publications de la gazette pour vous donner un aperçu rapide à partager en intro de réunion.</p>
            {!resume && !loading && (
              <button onClick={runResume} className="btn-primary flex items-center gap-1.5" data-testid="ai-resume-run">
                <Sparkles className="w-4 h-4" /> Générer le résumé
              </button>
            )}
            {loading && (
              <div className="py-8 grid place-items-center text-[#475569] text-sm">
                <Loader2 className="w-6 h-6 animate-spin mb-2" /> Claude analyse les publications…
              </div>
            )}
            {resume && (
              <div className="space-y-3" data-testid="ai-resume-result">
                <div className="bg-[#F9F9F8] rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap">{resume.resume}</div>
                <div className="text-xs text-[#94A3B8]">À partir de {resume.nb_posts} publication(s) récente(s)</div>
                <div className="flex gap-2">
                  <button onClick={() => navigator.clipboard?.writeText(resume.resume).then(() => toast.success('Copié'))} className="btn-secondary text-xs">Copier</button>
                  <button onClick={runResume} className="btn-secondary text-xs">Régénérer</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'sujets' && (
          <div data-testid="ai-sujets-pane">
            <p className="text-xs text-[#475569] mb-3">L'IA propose 3 sujets de prochaine réunion alignés avec les conversations récentes des channels et de la gazette.</p>
            {!sujets && !loading && (
              <button onClick={runSuggerer} className="btn-primary flex items-center gap-1.5" data-testid="ai-sujets-run">
                <Sparkles className="w-4 h-4" /> Générer 3 sujets
              </button>
            )}
            {loading && (
              <div className="py-8 grid place-items-center text-[#475569] text-sm">
                <Loader2 className="w-6 h-6 animate-spin mb-2" /> Claude réfléchit…
              </div>
            )}
            {sujets && (
              <div className="space-y-3" data-testid="ai-sujets-result">
                {sujets.map((s, i) => (
                  <div key={i} className="card !p-3 !shadow-none !border-[#E2E8F0]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[#0F172A] text-sm">{s.titre}</div>
                        <div className="text-xs text-[#475569] mt-1">{s.description}</div>
                      </div>
                      {s.format && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#0F172A] shrink-0">{s.format}</span>}
                    </div>
                  </div>
                ))}
                <button onClick={runSuggerer} className="btn-secondary text-xs">Régénérer</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, currentUser, canModerate, onChanged }) {
  const [comments, setComments] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [maReaction, setMaReaction] = useState(post.ma_reaction);
  const [reactions, setReactions] = useState(post.reactions || {});

  const loadComments = () => api.get(`/gazette/${post.id}/comments`).then((r) => setComments(r.data));
  const toggleComments = () => { if (!showComments && !comments) loadComments(); setShowComments(!showComments); };

  const react = async (type) => {
    try {
      const { data } = await api.post(`/gazette/${post.id}/reaction`, { type });
      if (data.action === 'removed') {
        setReactions({ ...reactions, [type]: Math.max(0, (reactions[type] || 1) - 1) });
        setMaReaction(null);
      } else {
        const newReacts = { ...reactions };
        if (maReaction) newReacts[maReaction] = Math.max(0, (newReacts[maReaction] || 1) - 1);
        newReacts[type] = (newReacts[type] || 0) + 1;
        setReactions(newReacts);
        setMaReaction(type);
      }
    } catch { toast.error('Erreur'); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try { await api.delete(`/gazette/${post.id}`); toast.success('Supprimé'); onChanged(); }
    catch { toast.error('Erreur'); }
  };

  const epingler = async () => {
    try { await api.patch(`/gazette/${post.id}/epingler`, { epinglé: !post.epinglé }); onChanged(); }
    catch { toast.error('Erreur'); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentInput.trim()) return;
    try {
      const { data } = await api.post(`/gazette/${post.id}/comments`, { contenu: commentInput });
      setComments([...(comments || []), data]);
      setCommentInput('');
    } catch { toast.error('Erreur'); }
  };

  const removeComment = async (cId) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/gazette/comments/${cId}`); setComments(comments.filter((c) => c.id !== cId)); }
    catch { toast.error('Erreur'); }
  };

  const isAuthor = post.author_id === currentUser?.id;

  return (
    <div className="card !p-0 overflow-hidden" data-testid={`post-${post.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full grid place-items-center text-white font-black text-sm shrink-0" style={{ background: 'var(--theme-primary)' }}>
              {(post.author_nom?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <div className="font-bold text-sm text-[#0F172A]">{post.author_nom}</div>
              <div className="text-[10px] text-[#475569] capitalize">{post.author_role?.replace('_', ' ')} · {new Date(post.created_at).toLocaleString('fr-FR')}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {post.epinglé && <Pin className="w-3.5 h-3.5 text-amber-500 fill-current" />}
            {canModerate && (
              <button onClick={epingler} className="text-[#94A3B8] hover:text-amber-500 p-1" title={post.epinglé ? 'Désépingler' : 'Épingler'} data-testid={`post-pin-${post.id}`}>
                <Pin className={`w-3.5 h-3.5 ${post.epinglé ? 'fill-current text-amber-500' : ''}`} />
              </button>
            )}
            {(isAuthor || canModerate) && (
              <button onClick={remove} className="text-[#94A3B8] hover:text-rose-600 p-1" title="Supprimer" data-testid={`post-delete-${post.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {post.contenu && <p className="text-sm text-[#0F172A] whitespace-pre-wrap leading-relaxed mb-3">{post.contenu}</p>}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.tags.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#0F172A]">#{t}</span>)}
          </div>
        )}

        {post.image_url && (
          <img src={post.image_url} alt="" className="w-full rounded-lg max-h-96 object-cover mb-3 bg-[#F1F5F9]" />
        )}

        <div className="flex items-center gap-1 -ml-1">
          {REACTIONS.map((r) => {
            const Ic = r.icon;
            const active = maReaction === r.type;
            const count = reactions[r.type] || 0;
            return (
              <button key={r.type} onClick={() => react(r.type)}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${active ? 'bg-[#F1F5F9]' : 'hover:bg-[#F9F9F8]'}`}
                style={active ? { color: r.color } : {}}
                data-testid={`reaction-${r.type}-${post.id}`}
                title={r.label}
              >
                <Ic className={`w-3.5 h-3.5 ${active ? 'fill-current' : ''}`} />
                {count > 0 && <span className="font-bold">{count}</span>}
              </button>
            );
          })}
          <button onClick={toggleComments} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs hover:bg-[#F9F9F8] text-[#475569] ml-auto" data-testid={`comments-toggle-${post.id}`}>
            <MessageSquare className="w-3.5 h-3.5" /> {post.nb_comments || 0}
          </button>
        </div>
      </div>

      {showComments && (
        <div className="border-t border-[#F1F5F9] bg-[#F9F9F8] p-3">
          {comments === null ? <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#94A3B8]" /></div> :
            comments.length === 0 ? <div className="text-center text-xs text-[#94A3B8] py-3">Aucun commentaire.</div> :
            <div className="space-y-2 mb-2">
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2 group" data-testid={`comment-${c.id}`}>
                  <div className="w-7 h-7 rounded-full grid place-items-center text-white font-black text-[10px] shrink-0" style={{ background: 'var(--theme-primary)' }}>
                    {(c.author_nom?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="bg-white rounded-lg px-2.5 py-1.5 flex-1 text-xs">
                    <div className="font-bold text-[#0F172A]">{c.author_nom}</div>
                    <div className="text-[#0F172A] whitespace-pre-wrap">{c.contenu}</div>
                  </div>
                  {(c.author_id === currentUser?.id || canModerate) && (
                    <button onClick={() => removeComment(c.id)} className="opacity-0 group-hover:opacity-100 text-[#94A3B8] hover:text-rose-600 p-1" data-testid={`comment-delete-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          }
          <form onSubmit={submitComment} className="flex gap-2 mt-2">
            <input value={commentInput} onChange={(e) => setCommentInput(e.target.value)} placeholder="Ajouter un commentaire..." className="input-base !py-1.5 text-xs" data-testid={`comment-input-${post.id}`} />
            <button type="submit" className="btn-primary !py-1.5 px-2.5" data-testid={`comment-send-${post.id}`}><Send className="w-3.5 h-3.5" /></button>
          </form>
        </div>
      )}
    </div>
  );
}

function ComposeModal({ onClose, onSaved }) {
  const [contenu, setContenu] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!contenu.trim() && !imageUrl) return toast.error('Ajoutez du texte ou une image');
    setSaving(true);
    try {
      await api.post('/gazette', {
        contenu, image_url: imageUrl || undefined,
        tags: tags.split(/[,\s]+/).filter(Boolean).slice(0, 5),
      });
      toast.success('Publication ajoutée');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-lg w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="compose-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-[#0F172A]">Nouvelle publication</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
        </div>
        <textarea
          rows={5} value={contenu} onChange={(e) => setContenu(e.target.value)}
          placeholder="Quoi de neuf ?" className="input-base mb-3" maxLength={5000}
          data-testid="compose-contenu"
        />
        <div className="space-y-2 mb-3">
          <ImageUploader
            value={imageUrl}
            onChange={(url) => setImageUrl(url)}
            label="Image (optionnel)"
            testid="compose-image-uploader"
            hint="PNG/JPG/WEBP · 5 Mo max"
            aspect="16/9"
          />
        </div>
        <div>
          <label className="label-uppercase block mb-1.5">Tags (séparés par virgule, max 5)</label>
          <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} className="input-base" placeholder="Ex: reunion, networking, conseil" data-testid="compose-tags" />
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="compose-submit">{saving ? 'Publication…' : 'Publier'}</button>
        </div>
      </form>
    </div>
  );
}
